import {
    HttpBackend,
    HttpClient,
    HttpErrorResponse,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap, tap } from 'rxjs/operators';
import { LoaderService } from '../services/loader/loader-service';
import { NotificationsService } from '../services/notifications/notifications-service';
import { SKIP_ERROR_TOAST } from './http-context-tokens';
import { environment } from 'src/environments/environment';

@Injectable()
export class HttpRequestsInterceptor implements HttpInterceptor {
    private readonly _apiOrigin = environment.API_ORIGIN;
    private readonly _authOrigin = environment.AUTH_ORIGIN;
    private readonly _azureCoreUrl = environment.AZURE_CORE_URL;
    private readonly _azureMeasurementsUrl = environment.AZURE_MEASUREMENTS_URL;
    private _refresh$: Observable<{ access_token: string; refresh_token: string }> | null = null;
    constructor(
        private _notificationsService: NotificationsService,
        private _loaderService: LoaderService,
        private _backend: HttpBackend
    ) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler) {
        let url = req.url;
        let fullUrlReq: HttpRequest<unknown> = req.clone();
        const locationId = this.getLocationIdFromUrl();

        if (!fullUrlReq.url.includes('http') && fullUrlReq.url.includes('auth')) {
            fullUrlReq = req.clone({
                headers: req.headers.set('Authorization', `Bearer ${this.getToken()}`),
                url: this._authOrigin + url
            });
        } else if (
            !fullUrlReq.url.includes('http') &&
            !fullUrlReq.url.startsWith('assets') &&
            !fullUrlReq.url.includes('auth')
        ) {
            //console.log('not auth');
            if (url.startsWith('/placeholder')) {
                url = url.replace('/placeholder', '');
            }

            let negotiateUrl = `${this._azureCoreUrl}/${req.url}`;
            if (locationId) {
                const separator = negotiateUrl.includes('?') ? '&' : '?';
                negotiateUrl = `${negotiateUrl}${separator}locationId=${encodeURIComponent(locationId)}`;
            }

            fullUrlReq = req.clone({
                headers: req.headers.set('Authorization', `Bearer ${this.getToken()}`).set('Web', 'true'),
                withCredentials: true,
                // url: this._apiOrigin + url,
                url:
                    req.url === 'negotiate'
                        ? negotiateUrl //&macAddress=C164D1FEFF2C6A3C'
                        : fullUrlReq.url.includes('measurements')
                          ? `${this._azureMeasurementsUrl}${req.url}`
                          : `${this._apiOrigin}${req.url}`
            });
        }

        const toRun = next.handle(fullUrlReq);

        this._loaderService.show();
        return toRun.pipe(
            catchError((error: HttpErrorResponse) => {
                const isAuthCall = fullUrlReq.url.includes('/auth/refresh') || fullUrlReq.url.includes('/auth/signin');
                const expired =
                    error.status === 498 || ([401, 403].includes(error.status) && this._accessTokenExpired());
                if (expired && !isAuthCall && this.getRefreshToken()) {
                    return this._refreshToken(next, fullUrlReq);
                }
                if (!req.context.get(SKIP_ERROR_TOAST)) {
                    this._notificationsService.displayHttpError(error.error);
                }
                return throwError(() => error);
            }),
            finalize(() => {
                this._loaderService.hide();
            })
        );
    }

    private getLocationIdFromUrl(): string | null {
        if (typeof window === 'undefined') {
            return null;
        }

        const params = new URLSearchParams(window.location.search);
        return params.get('locationId');
    }

    private _refreshToken(next: HttpHandler, req: HttpRequest<unknown>) {
        return this._refreshTokens().pipe(
            switchMap(() =>
                next.handle(req.clone({ headers: req.headers.set('Authorization', `Bearer ${this.getToken()}`) }))
            ),
            catchError((refreshError) => {
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                sessionStorage.removeItem('access_token');
                sessionStorage.removeItem('refresh_token');
                location.href = '/login';
                return throwError(() => refreshError);
            })
        );
    }

    /** One in-flight refresh shared by all concurrent 401s; the auth server rotates the refresh token on every call. */
    private _refreshTokens() {
        if (!this._refresh$) {
            const storage = localStorage.getItem('refresh_token') ? localStorage : sessionStorage;
            // HttpBackend skips interceptors: the refresh call must not get the access token or trigger another refresh
            this._refresh$ = new HttpClient(this._backend)
                .post<{ access_token: string; refresh_token: string }>(
                    this._authOrigin + '/auth/refresh',
                    {},
                    { headers: { Authorization: `Bearer ${this.getRefreshToken()}` } }
                )
                .pipe(
                    tap((tokens) => {
                        storage.setItem('access_token', tokens.access_token);
                        storage.setItem('refresh_token', tokens.refresh_token);
                    }),
                    finalize(() => (this._refresh$ = null)),
                    shareReplay(1)
                );
        }
        return this._refresh$;
    }

    private _accessTokenExpired() {
        try {
            return JSON.parse(atob(this.getToken()!.split('.')[1])).exp * 1000 < Date.now();
        } catch {
            return false;
        }
    }

    getToken() {
        return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    }

    getRefreshToken() {
        return localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');
    }
}
